import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    Box,
    CircularProgress,
    Collapse,
    Link
} from '@mui/material';
import { HighlightOffOutlined as HighlightOffOutlinedIcon } from '@mui/icons-material';
import DataSourceService from '../../services/DataSourceService';

/**
 * Detalle de por qué falló una conexión de archivo: una entrada por hoja
 * con status "error" (sheet_progress, ver GET /sourceconnections/{id}),
 * con el mensaje para el usuario y el detalle técnico plegado. El listado
 * de conexiones solo trae contadores, por eso el detalle se pide al abrir.
 * Si no hay hojas con error (CSV, o fallo previo a procesar las hojas) se
 * muestra el error a nivel conexión.
 */
const SheetErrorsDialog = ({ open, onClose, source }) => {
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [failedSheets, setFailedSheets] = useState([]);
    const [connectionError, setConnectionError] = useState({ user: '', technical: '' });
    const [expanded, setExpanded] = useState({});
    // El polling de SemanticConnections.jsx recrea `source` cada pocos
    // segundos: el efecto depende solo del id para no recargar (ni
    // replegar el detalle) con el diálogo abierto.
    const sourceRef = useRef(source);
    sourceRef.current = source;
    const sourceId = source?.id;

    useEffect(() => {
        if (!open || !sourceId) return undefined;
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        setExpanded({});
        DataSourceService.getRawSourceConnection(sourceId)
            .then((raw) => {
                if (cancelled) return;
                const current = sourceRef.current || {};
                const sheets = Array.isArray(raw?.sheet_progress) ? raw.sheet_progress : [];
                setFailedSheets(sheets.filter((s) => s.status === 'error'));
                setConnectionError({
                    user: raw?.error_message_user || current.errorMessageUser || '',
                    technical: raw?.error_message || current.errorMessage || ''
                });
            })
            .catch(() => {
                if (!cancelled) setLoadError('No se pudo cargar el detalle del error. Intenta de nuevo.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, sourceId]);

    const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    const renderEntry = (key, title, userMessage, technical) => (
        <Box
            key={key}
            sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'var(--wb-x-bg-f4433614, rgba(244, 67, 54, 0.08))',
                border: '1px solid var(--wb-x-border-f443364c, rgba(244, 67, 54, 0.3))'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <HighlightOffOutlinedIcon sx={{ color: 'var(--wb-danger-strong)', mr: 0.75, width: 18 }} />
                <Typography variant="subtitle2" sx={{ color: 'var(--wb-text-strong)' }}>{title}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--wb-x-text-ffffffd9, rgba(255, 255, 255, 0.85))' }}>
                {userMessage || 'No se pudo procesar esta hoja.'}
            </Typography>
            {technical && (
                <>
                    <Link
                        component="button"
                        variant="caption"
                        onClick={() => toggle(key)}
                        sx={{ mt: 0.5, color: 'var(--wb-text-dim)' }}
                    >
                        {expanded[key] ? 'Ocultar detalle técnico' : 'Ver detalle técnico'}
                    </Link>
                    <Collapse in={!!expanded[key]}>
                        <Typography
                            variant="caption"
                            component="pre"
                            sx={{
                                mt: 0.5,
                                p: 1,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                bgcolor: 'var(--wb-x-bg-0000004c, rgba(0, 0, 0, 0.3))',
                                borderRadius: 1,
                                color: 'var(--wb-text-faint)'
                            }}
                        >
                            {technical}
                        </Typography>
                    </Collapse>
                </>
            )}
        </Box>
    );

    let content;
    if (loading) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: 'var(--wb-primary)' }} />
            </Box>
        );
    } else if (loadError) {
        content = <Typography variant="body2" sx={{ color: 'var(--wb-danger-strong)' }}>{loadError}</Typography>;
    } else if (failedSheets.length > 0) {
        content = (
            <>
                <Typography variant="body2" sx={{ mb: 2, color: 'var(--wb-text-soft)' }}>
                    {failedSheets.length === 1
                        ? '1 hoja no se pudo procesar:'
                        : `${failedSheets.length} hojas no se pudieron procesar:`}
                </Typography>
                {failedSheets.map((sheet) => renderEntry(
                    `sheet-${sheet.sheet_index}`,
                    `Hoja: ${sheet.sheet_name || `#${sheet.sheet_index + 1}`}`,
                    sheet.error_message_user,
                    sheet.error_message
                ))}
            </>
        );
    } else {
        content = renderEntry(
            'connection',
            source?.name || 'Conexión',
            connectionError.user || 'No se pudo procesar la conexión.',
            connectionError.technical
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { bgcolor: 'var(--wb-bg-panel)', color: 'var(--wb-text-strong)' } }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
                Detalle del error
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>{content}</DialogContent>
            <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)', p: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
                >
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SheetErrorsDialog;
